// Vendor imports
const Immutable = require('immutable')
// Common Client V2 imports
const {
  FeedItemMetaRecord,
  FeedStreamRecord
} = require('client-v2/common/containers/feed/records')
const ArticleModelRecord = require('client-v2/common/models/article/record')
const InterestModelRecord = require('client-v2/common/models/interest/record')
const {
  defaultReviver,
  keyedReviver,
  listReviver
} = require('client-v2/common/state/record-reviver')
const { FetchStateEnum } = require('client-v2/common/utils/enums')
// Container imports
const ArticleProfileCollectionsRecord = require('./collections-record')

// Enumerations
const TabNameEnum = {
  COLLECTIONS: 'COLLECTIONS',
  INTERESTS: 'INTERESTS'
}

class ArticleProfileSlideRecord extends Immutable.Record({
  article: null, // ArticleModelRecord
  articleMeta: new FeedItemMetaRecord(),
  articleState: FetchStateEnum.EMPTY,
  collectionsItems: Immutable.List(),
  collectionsMeta: Immutable.Map(),
  collectionsState: FetchStateEnum.EMPTY,
  curatedAt: null,
  curatedBy: null,
  interestsItems: Immutable.List(), // [ InterestModelRecord, ... ]
  interestsState: FetchStateEnum.EMPTY,
  relatedStream: null,
  selectedTab: TabNameEnum.COLLECTIONS
}) {

  addCollections({ items, meta }) {
    return this.mergeDeep({
      collectionsItems: Immutable.fromJS(items, collectionsItemsReviver),
      collectionsMeta: meta,
      collectionsState: FetchStateEnum.LOADED
    })
  }

  addInterests({ items }) {
    return this.mergeDeep({
      interestsItems: Immutable.fromJS(items, interestsItemsReviver),
      interestsState: FetchStateEnum.LOADED
    })
  }

  addRelated(feedStreamPage) {
    return this.update('relatedStream', relatedStream => {
      return (relatedStream || new FeedStreamRecord).appendNextPage(feedStreamPage)
    })
  }

  resetCollections() {
    return this.merge({
      collectionsItems: Immutable.List(),
      collectionsMeta: Immutable.Map(),
      collectionsState: FetchStateEnum.EMPTY
    })
  }

  setArticle(article) {
    return this.merge({
      article: Immutable.fromJS(article, ArticleModelRecord.reviver),
      articleState: FetchStateEnum.LOADED
    })
  }

  setCollectionsFailed() {
    if (this.collectionsItems.size) {
      // Prevent further pagination attempts
      return this.mergeDeep({
        collectionsMeta: { nextUrl: null },
        collectionsState: FetchStateEnum.LOADED
      })
    }
    return this.merge({
      collectionsState: FetchStateEnum.FAILED
    })
  }

  setCollectionsLoading() {
    return this.merge({
      collectionsState: FetchStateEnum.LOADING
    })
  }

  setCurator({ curatedAt, curatedBy }) {
    return this.merge({ curatedAt, curatedBy })
  }

  setInterestsFailed() {
    return this.merge({
      interestsState: FetchStateEnum.FAILED
    })
  }

  setInterestsLoading() {
    return this.merge({
      interestsState: FetchStateEnum.LOADING
    })
  }

  setRelatedFailed() {
    const relatedStream = this.relatedStream || new FeedStreamRecord
    return this.merge({
      relatedStream: relatedStream.setNextPageFailed()
    })
  }

  setRelatedLoading() {
    const relatedStream = this.relatedStream || new FeedStreamRecord
    return this.merge({
      relatedStream: relatedStream.setNextPageLoading()
    })
  }

  setSelectedTab(value) {
    const selectedTab = TabNameEnum[value]
    if (selectedTab) {
      return this.merge({ selectedTab })
    }
    throw new Error(`Invalid TabNameEnum value: ${value}`)
  }
}

// Record revivers
const collectionsItemsReviver = listReviver(ArticleProfileCollectionsRecord.reviver)
const interestsItemsReviver = listReviver(InterestModelRecord.reviver)
const reviver = keyedReviver(ArticleProfileSlideRecord, {
  article: ArticleModelRecord.reviver,
  articleMeta: FeedItemMetaRecord.reviver,
  collectionsItems: collectionsItemsReviver,
  collectionsMeta: defaultReviver,
  interestsItems: interestsItemsReviver,
  relatedStream: FeedStreamRecord.reviver
})

module.exports = Object.assign(ArticleProfileSlideRecord, {
  // Enumerations
  TabNameEnum,
  // Record revivers
  collectionsItemsReviver,
  interestsItemsReviver,
  reviver
})
